import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Standalone free-gift group toggle for cart line items.
 * Gift rows render collapsed via Liquid; this only handles expand/collapse.
 *
 * Also removes orphan gift lines after cart updates. Kaching gifts often stay in
 * the cart when their Subscribe & Save parent is removed (especially after the
 * Bundle & Save add → remove Spreadatine flow). Orphans then block new S&S
 * lines from receiving their own free gifts.
 */

class GrCartGiftGroup extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    this.#button = this.querySelector('[data-gr-gifts-toggle]');
    if (!(this.#button instanceof HTMLButtonElement)) return;

    this.#button.addEventListener('click', this.#onToggle);

    // Default collapsed. Defer so sibling gift rows are in the DOM.
    const syncCollapsed = () => this.#apply(false);
    queueMicrotask(syncCollapsed);
    requestAnimationFrame(syncCollapsed);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onToggle);
    this.#button = null;
  }

  #onToggle = () => {
    if (!(this.#button instanceof HTMLButtonElement)) return;
    const nextOpen = this.#button.getAttribute('aria-expanded') !== 'true';
    this.#apply(nextOpen);
  };

  /** @param {boolean} open */
  #apply(open) {
    if (!(this.#button instanceof HTMLButtonElement)) return;

    this.#button.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.classList.toggle('is-open', open);

    for (const row of this.#giftRows()) {
      row.classList.toggle('cart-items__table-row--gift-collapsed', !open);
    }
  }

  /** @returns {HTMLElement[]} */
  #giftRows() {
    const toggleRow = this.closest('tr');
    if (!(toggleRow instanceof HTMLElement)) return [];

    /** @type {HTMLElement[]} */
    const rows = [];
    let sibling = toggleRow.nextElementSibling;

    while (sibling instanceof HTMLElement) {
      if (sibling.classList.contains('cart-items__table-row--gift-toggle')) break;
      if (sibling.classList.contains('cart-items__table-row--gift')) {
        rows.push(sibling);
        sibling = sibling.nextElementSibling;
        continue;
      }
      if (sibling.classList.contains('cart-items__table-row')) break;
      sibling = sibling.nextElementSibling;
    }

    return rows;
  }
}

if (!customElements.get('gr-cart-gift-group')) {
  customElements.define('gr-cart-gift-group', GrCartGiftGroup);
}

/** @type {boolean} */
let giftCleanupRunning = false;

/** @type {ReturnType<typeof setTimeout> | null} */
let giftCleanupTimer = null;

/** @type {boolean} */
let giftCleanupQueued = false;

/**
 * @param {Record<string, unknown>} item
 * @returns {boolean}
 */
function isLikelyGiftLine(item) {
  const price = Number(item.final_line_price ?? item.line_price ?? -1);
  if (price !== 0) return false;

  const title = `${item.product_title || ''} ${item.title || ''}`;
  if (/free/i.test(title)) return true;

  const properties = /** @type {Record<string, string> | undefined} */ (item.properties);
  if (properties) {
    for (const [key, value] of Object.entries(properties)) {
      const blob = `${key} ${value}`.toLowerCase();
      if (blob.includes('gift') || blob.includes('free')) return true;
    }
  }

  // $0 line with no selling plan is the usual Kaching gift shape even without "FREE" in the title.
  return !item.selling_plan_allocation;
}

/**
 * A gift run is valid only if some Subscribe & Save line appears before it in the cart.
 * (Not only the immediate previous line — paid CHARGE can sit between S&S and gifts.)
 * @param {Record<string, unknown>[]} items
 * @param {number} runStart
 * @returns {boolean}
 */
function giftRunHasSubscriptionBefore(items, runStart) {
  for (let i = 0; i < runStart; i++) {
    if (isLikelyGiftLine(items[i])) continue;
    if (items[i].selling_plan_allocation) return true;
  }
  return false;
}

/**
 * @param {{ items?: Record<string, unknown>[] }} cart
 * @returns {string[]}
 */
function getOrphanGiftKeys(cart) {
  const items = cart.items || [];
  /** @type {string[]} */
  const keys = [];

  let i = 0;
  while (i < items.length) {
    const item = items[i];
    if (!isLikelyGiftLine(item)) {
      i += 1;
      continue;
    }

    const anchored = giftRunHasSubscriptionBefore(items, i);

    let j = i;
    while (j < items.length && isLikelyGiftLine(items[j])) {
      if (!anchored) {
        const key = String(items[j].key || '');
        if (key) keys.push(key);
      }
      j += 1;
    }
    i = j;
  }

  return keys;
}

/** @returns {string[]} */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  }
  return ids;
}

async function fetchCart() {
  const response = await fetch(Theme.routes.cart_url, {
    headers: { Accept: 'application/json' },
  });
  return response.json();
}

/**
 * @param {string[]} keys
 */
async function removeGiftKeys(keys) {
  if (!keys.length || giftCleanupRunning) {
    if (keys.length) giftCleanupQueued = true;
    return;
  }

  giftCleanupRunning = true;

  try {
    const sectionIds = getCartSectionIds();
    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: keys.map((id) => ({ id, quantity: 0 })),
        promise: deferred.promise,
      })
    );

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();
    const cart = await fetchCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        itemCount: 0,
        source: 'gr-cart-gift-cleanup',
        sections: data.sections,
      },
    });
  } catch (error) {
    console.warn('[gr-cart-gift-group] orphan gift cleanup failed', error);
  } finally {
    giftCleanupRunning = false;
    if (giftCleanupQueued) {
      giftCleanupQueued = false;
      queueMicrotask(() => {
        void cleanupOrphanGifts();
      });
    }
  }
}

async function cleanupOrphanGifts() {
  if (giftCleanupRunning) {
    giftCleanupQueued = true;
    return;
  }

  try {
    const cart = await fetchCart();
    const orphanKeys = getOrphanGiftKeys(cart);
    if (orphanKeys.length) {
      await removeGiftKeys(orphanKeys);
    }
  } catch (error) {
    console.warn('[gr-cart-gift-group] orphan gift check failed', error);
  }
}

/**
 * @param {CartLinesUpdateEvent} event
 */
function onCartLinesUpdate(event) {
  event.promise
    ?.then(({ detail }) => {
      if (detail?.source === 'gr-cart-gift-cleanup') return;

      if (giftCleanupTimer) clearTimeout(giftCleanupTimer);

      // Immediate pass: clear orphans before a fast follow-up S&S add can be blocked.
      void cleanupOrphanGifts();

      // Second pass: Kaching often mutates the cart shortly after ours.
      giftCleanupTimer = setTimeout(() => {
        void cleanupOrphanGifts();
      }, 500);
    })
    .catch(() => {
      /* ignore aborted cart updates */
    });
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);

// Catch orphans already present when the cart drawer scripts load.
queueMicrotask(() => {
  setTimeout(() => {
    void cleanupOrphanGifts();
  }, 600);
});
